// Backend integration point: replace this in-memory store with API calls to your backend

export type IngredientStatus = 'ok' | 'low' | 'critical' | 'out';
export type ProductType = 'Bebida' | 'Comida' | 'Extra' | 'Empaque';
export type BebidaSize = '8oz' | '12oz' | '16oz';
export type SalesChannel = 'ventaDirecta' | 'whatsappInstagram' | 'pedidosYa' | 'otro';

export interface Ingredient {
  id: string;
  name: string;
  unit: 'g' | 'ml' | 'unidad';
  bagContent: number;
  bagPrice: number;
  unitCost: number;
  dose: number;
  doseCost: number;
  stock: number;
  minStock: number;
  provider: string;
  lastUpdated: string;
  status: IngredientStatus;
}

export interface RecipeIngredient {
  ingredientId: string;
  ingredientName: string;
  dose: number;
  unit: string;
  unitCost: number;
  doseCost: number;
}

export interface SizeConfig {
  enabled: boolean;
  recipe: RecipeIngredient[];
  totalCost: number;
  prices: {
    ventaDirecta: number;
    whatsappInstagram: number;
    pedidosYa: number;
    otro: number;
  };
}

export interface Product {
  id: string;
  name: string;
  productType: ProductType;
  imageEmoji: string;
  active: boolean;
  // For Bebida: size-based config
  sizes?: {
    '8oz': SizeConfig;
    '12oz': SizeConfig;
    '16oz': SizeConfig;
  };
  // For Comida / Extra / Empaque: general recipe + prices
  recipe?: RecipeIngredient[];
  totalCost?: number;
  prices?: {
    ventaDirecta: number;
    whatsappInstagram: number;
    pedidosYa: number;
    otro: number;
  };
  // Legacy / computed
  pedidosYaCommission: number;
}

export interface SaleItem {
  productId: string;
  productName: string;
  size?: BebidaSize;
  quantity: number;
  unitPrice: number;
  notes: string;
  subtotal: number;
}

export interface PackagingItem {
  productId: string;
  productName: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
}

export interface Sale {
  id: string;
  receiptNumber: string;
  date: string;
  time: string;
  customerName?: string;
  items: SaleItem[];
  packagingItems: PackagingItem[];
  subtotal: number;
  discount: number;
  deliveryChargedToCustomer: number;
  deliveryAbsorbedByBunori: number;
  pedidosYaCommission: number;
  packagingCost: number;
  total: number;
  realProfit: number;
  paymentMethod: string;
  salesChannel: SalesChannel;
}

export interface Purchase {
  id: string;
  date: string;
  ingredientId: string;
  ingredientName: string;
  provider: string;
  quantity: number;
  unit: string;
  price: number;
  invoice: string;
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: 'Administrador' | 'Empleado';
  passwordHash: string;
  active: boolean;
  createdAt: string;
}

// ─── INITIAL INGREDIENTS ───────────────────────────────────────────────────
const initialIngredients: Ingredient[] = [];

function calcStatus(ing: Omit<Ingredient, 'status'>): IngredientStatus {
  if (ing.stock <= 0) return 'out';
  if (ing.stock <= ing.minStock * 0.5) return 'critical';
  if (ing.stock <= ing.minStock) return 'low';
  return 'ok';
}

const ings = initialIngredients;

function buildRecipeFromIds(ids: string[]): RecipeIngredient[] {
  return ids.map((id) => {
    const ing = ings.find((i) => i.id === id)!;
    return {
      ingredientId: ing.id,
      ingredientName: ing.name,
      dose: ing.dose,
      unit: ing.unit,
      unitCost: ing.unitCost,
      doseCost: ing.doseCost,
    };
  });
}

function calcRecipeCost(recipe: RecipeIngredient[]): number {
  return recipe.reduce((s, r) => s + r.doseCost, 0);
}

function emptyPrices() {
  return { ventaDirecta: 0, whatsappInstagram: 0, pedidosYa: 0, otro: 0 };
}

function emptySizeConfig(): SizeConfig {
  return { enabled: false, recipe: [], totalCost: 0, prices: emptyPrices() };
}

// ─── INITIAL PRODUCTS ──────────────────────────────────────────────────────
const initialProducts: Product[] = [];

// ─── INITIAL SALES ──────────────────────────────────────────────────────────
const initialSales: Sale[] = [];

const initialPurchases: Purchase[] = [];

// ─── INITIAL USER PROFILES ─────────────────────────────────────────────────
const initialUserProfiles: UserProfile[] = [
  {
    id: 'user-001',
    name: 'Administrador',
    email: 'admin@bunori.com',
    role: 'Administrador',
    passwordHash: 'admin123',
    active: true,
    createdAt: '2026-01-01',
  },
];

// ─── SIMPLE IN-MEMORY STORE ─────────────────────────────────────────────────
let _ingredients: Ingredient[] = initialIngredients.map((i) => ({ ...i, status: calcStatus(i) }));
let _products: Product[] = initialProducts;
let _sales: Sale[] = initialSales;
let _purchases: Purchase[] = initialPurchases;
let _userProfiles: UserProfile[] = initialUserProfiles;
let _receiptCounter = 244;
let _currentUser: UserProfile | null = null;

function recalcIngredientStatus(ing: Ingredient): Ingredient {
  return { ...ing, status: calcStatus(ing) };
}

export const store = {
  // ── Auth ─────────────────────────────────────────────────────────────────
  login: (email: string, password: string): UserProfile | null => {
    const user = _userProfiles.find(
      (u) => u.email.toLowerCase() === email.toLowerCase() && u.passwordHash === password && u.active
    );
    if (user) {
      _currentUser = user;
      if (typeof window !== 'undefined') {
        localStorage.setItem('bunori_current_user', JSON.stringify(user));
      }
    }
    return user || null;
  },

  logout: () => {
    _currentUser = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('bunori_current_user');
    }
  },

  getCurrentUser: (): UserProfile | null => {
    if (_currentUser) return _currentUser;
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('bunori_current_user');
      if (stored) {
        try {
          _currentUser = JSON.parse(stored);
          return _currentUser;
        } catch {
          return null;
        }
      }
    }
    return null;
  },

  // ── User Profiles ─────────────────────────────────────────────────────────
  getUserProfiles: (): UserProfile[] => [..._userProfiles],

  addUserProfile: (data: Omit<UserProfile, 'id' | 'createdAt'>): UserProfile => {
    const newUser: UserProfile = {
      ...data,
      id: `user-${Date.now()}`,
      createdAt: new Date().toISOString().split('T')[0],
    };
    _userProfiles = [..._userProfiles, newUser];
    return newUser;
  },

  updateUserProfile: (id: string, data: Partial<UserProfile>): UserProfile | null => {
    const idx = _userProfiles.findIndex((u) => u.id === id);
    if (idx === -1) return null;
    const updated = { ..._userProfiles[idx], ...data };
    _userProfiles = _userProfiles.map((u) => (u.id === id ? updated : u));
    return updated;
  },

  deleteUserProfile: (id: string): boolean => {
    _userProfiles = _userProfiles.filter((u) => u.id !== id);
    return true;
  },

  // ── Ingredients ──────────────────────────────────────────────────────────
  getIngredients: (): Ingredient[] => [..._ingredients],

  addIngredient: (data: Omit<Ingredient, 'id' | 'status' | 'unitCost' | 'doseCost'>): Ingredient => {
    const unitCost = data.bagContent > 0 ? data.bagPrice / data.bagContent : 0;
    const doseCost = unitCost * data.dose;
    const newIng: Ingredient = {
      ...data,
      id: `ing-${String(Date.now()).slice(-6)}`,
      unitCost,
      doseCost,
      status: calcStatus({ ...data, unitCost, doseCost }),
    };
    _ingredients = [..._ingredients, newIng];
    return newIng;
  },

  updateIngredient: (id: string, data: Partial<Ingredient>): Ingredient | null => {
    const idx = _ingredients.findIndex((i) => i.id === id);
    if (idx === -1) return null;
    const updated = recalcIngredientStatus({ ..._ingredients[idx], ...data });
    _ingredients = _ingredients.map((i) => (i.id === id ? updated : i));
    return updated;
  },

  updateIngredientPrice: (
    id: string,
    newBagContent: number,
    newBagPrice: number,
    addQuantity: number,
    provider: string,
    invoice: string,
    date: string
  ): Ingredient | null => {
    const idx = _ingredients.findIndex((i) => i.id === id);
    if (idx === -1) return null;
    const ing = _ingredients[idx];
    const unitCost = newBagContent > 0 ? newBagPrice / newBagContent : ing.unitCost;
    const doseCost = unitCost * ing.dose;
    const newStock = ing.stock + addQuantity;
    const updated = recalcIngredientStatus({
      ...ing,
      bagContent: newBagContent,
      bagPrice: newBagPrice,
      unitCost,
      doseCost,
      stock: newStock,
      provider,
      lastUpdated: date,
    });
    _ingredients = _ingredients.map((i) => (i.id === id ? updated : i));
    _purchases = [
      {
        id: `pur-${Date.now()}`,
        date,
        ingredientId: id,
        ingredientName: ing.name,
        provider,
        quantity: addQuantity,
        unit: ing.unit,
        price: newBagPrice,
        invoice,
      },
      ..._purchases,
    ];
    return updated;
  },

  deleteIngredient: (id: string): boolean => {
    _ingredients = _ingredients.filter((i) => i.id !== id);
    return true;
  },

  // ── Products ─────────────────────────────────────────────────────────────
  getProducts: (): Product[] => [..._products],

  addProduct: (data: Omit<Product, 'id'>): Product => {
    const newProd: Product = {
      ...data,
      id: `prod-${String(Date.now()).slice(-6)}`,
    };
    _products = [..._products, newProd];
    return newProd;
  },

  updateProduct: (id: string, data: Partial<Product>): Product | null => {
    const idx = _products.findIndex((p) => p.id === id);
    if (idx === -1) return null;
    const updated = { ..._products[idx], ...data };
    _products = _products.map((p) => (p.id === id ? updated : p));
    return updated;
  },

  deleteProduct: (id: string): boolean => {
    _products = _products.filter((p) => p.id !== id);
    return true;
  },

  // ── Sales ────────────────────────────────────────────────────────────────
  getSales: (): Sale[] => [..._sales],

  addSale: (saleData: Omit<Sale, 'id' | 'receiptNumber'>): Sale => {
    const receiptNumber = `REC-0${_receiptCounter++}`;
    const newSale: Sale = {
      ...saleData,
      id: `sale-${Date.now()}`,
      receiptNumber,
    };
    _sales = [newSale, ..._sales];

    // Deduct inventory for regular items
    saleData.items.forEach((item) => {
      const product = _products.find((p) => p.id === item.productId);
      if (!product) return;
      let recipe: RecipeIngredient[] = [];
      if (product.productType === 'Bebida' && item.size && product.sizes) {
        recipe = product.sizes[item.size]?.recipe || [];
      } else {
        recipe = product.recipe || [];
      }
      recipe.forEach((ri) => {
        const ingIdx = _ingredients.findIndex((i) => i.id === ri.ingredientId);
        if (ingIdx === -1) return;
        const newStock = Math.max(0, _ingredients[ingIdx].stock - ri.dose * item.quantity);
        const updated = recalcIngredientStatus({ ..._ingredients[ingIdx], stock: newStock });
        _ingredients = _ingredients.map((i) => (i.id === ri.ingredientId ? updated : i));
      });
    });

    // Deduct inventory for packaging items
    saleData.packagingItems.forEach((pkgItem) => {
      const product = _products.find((p) => p.id === pkgItem.productId);
      if (!product || !product.recipe) return;
      product.recipe.forEach((ri) => {
        const ingIdx = _ingredients.findIndex((i) => i.id === ri.ingredientId);
        if (ingIdx === -1) return;
        const newStock = Math.max(0, _ingredients[ingIdx].stock - ri.dose * pkgItem.quantity);
        const updated = recalcIngredientStatus({ ..._ingredients[ingIdx], stock: newStock });
        _ingredients = _ingredients.map((i) => (i.id === ri.ingredientId ? updated : i));
      });
    });

    return newSale;
  },

  // ── Purchases ────────────────────────────────────────────────────────────
  getPurchases: (): Purchase[] => [..._purchases],

  // ── Stats ────────────────────────────────────────────────────────────────
  getTodayStats: () => {
    const today = '2026-08-26';
    const todaySales = _sales.filter((s) => s.date === today);
    const totalSales = todaySales.reduce((s, sale) => s + sale.total, 0);
    const totalProfit = todaySales.reduce((s, sale) => s + sale.realProfit, 0);
    const totalCosts = totalSales - totalProfit;
    const totalCommissions = todaySales.reduce((s, sale) => s + sale.pedidosYaCommission, 0);
    const numSales = todaySales.length;

    const productCount: Record<string, { name: string; count: number; revenue: number }> = {};
    todaySales.forEach((sale) => {
      sale.items.forEach((item) => {
        if (!productCount[item.productId]) {
          productCount[item.productId] = { name: item.productName, count: 0, revenue: 0 };
        }
        productCount[item.productId].count += item.quantity;
        productCount[item.productId].revenue += item.subtotal;
      });
    });
    const topProduct = Object.values(productCount).sort((a, b) => b.count - a.count)[0] || null;
    const lowStockIngredients = _ingredients.filter((i) => i.status === 'low' || i.status === 'critical' || i.status === 'out');

    return {
      totalSales,
      totalProfit,
      totalCosts,
      totalCommissions,
      numSales,
      topProduct,
      lowStockIngredients,
      todaySales,
    };
  },

  getHourlySalesData: () => {
    const today = '2026-08-26';
    const todaySales = _sales.filter((s) => s.date === today);
    const hours = Array.from({ length: 12 }, (_, i) => {
      const hour = i + 7;
      const label = `${hour}:00`;
      const salesInHour = todaySales.filter((s) => {
        const h = parseInt(s.time.split(':')[0]);
        return h === hour;
      });
      return {
        hour: label,
        ventas: salesInHour.reduce((s, sale) => s + sale.total, 0),
        ganancia: salesInHour.reduce((s, sale) => s + sale.realProfit, 0),
      };
    });
    return hours;
  },

  getTopProductsData: () => {
    const today = '2026-08-26';
    const todaySales = _sales.filter((s) => s.date === today);
    const productCount: Record<string, { name: string; count: number }> = {};
    todaySales.forEach((sale) => {
      sale.items.forEach((item) => {
        if (!productCount[item.productId]) {
          productCount[item.productId] = { name: item.productName, count: 0 };
        }
        productCount[item.productId].count += item.quantity;
      });
    });
    return Object.values(productCount)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  },
};